import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import { jwtDecode } from "jwt-decode";
import { FaCalendarDays, FaGlobe, FaHeart, FaLocationDot, FaPhone, FaSnowflake, FaTruckFast, FaWarehouse } from "react-icons/fa6";
import { buildApiUrl, buildAssetUrl, formatList } from "../utils/api";
import { contactMethodOptions, productCategoryOptions, serviceNeedOptions, storageTypeOptions, timelineOptions } from "../utils/marketplaceOptions";

const initialQuote = {
  warehouseName: "",
  warehouseLocation: "",
  warehouseArea: "",
  userName: "",
  userEmail: "",
  userPhone: "",
  totalSKU: "",
  averageMonthlyOrders: "",
  salesChannel: "",
  companyName: "",
  companyWebsite: "",
  serviceNeeds: "",
  fulfillmentRegions: "",
  productCategory: "",
  storageType: "",
  temperatureRequirement: "",
  preferredGoLiveDate: "",
  preferredContactMethod: "Email",
  notes: "",
  userId: ""
};

function WarehouseDetailsMarketplace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [warehouse, setWarehouse] = useState(null);
  const [quoteForm, setQuoteForm] = useState(initialQuote);
  const [showModal, setShowModal] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const fetchWarehouse = async () => {
      try {
        const response = await axios.get(buildApiUrl(`/api/warehouses/${id}`));
        setWarehouse(response.data);
        setQuoteForm((previous) => ({
          ...previous,
          warehouseName: response.data.name,
          warehouseLocation: response.data.locations,
          warehouseArea: response.data.totalArea
        }));
      } catch (error) {
        console.error("Error fetching warehouse details:", error);
      }
    };

    const token = localStorage.getItem("token");
    if (token) {
      const decoded = jwtDecode(token);
      setQuoteForm((previous) => ({ ...previous, userId: decoded.id }));
    }

    fetchWarehouse();
  }, [id]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setQuoteForm((previous) => ({ ...previous, [name]: value }));
  };

  const handleWishlist = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token || !warehouse) {
        navigate("/userLogin");
        return;
      }

      let decoded;
      try { decoded = jwtDecode(token); } catch { navigate("/userLogin"); return; }

      await axios.post(buildApiUrl("/api/userWishList/create"), {
        userId: decoded.id,
        warehouseId: warehouse._id,
        warehouseName: warehouse.name,
        location: warehouse.locations,
        area: warehouse.totalArea
      });

      setSubmitStatus({ text: "Warehouse added to your saved list.", type: "success" });
    } catch (error) {
      console.error("Error saving warehouse:", error);
      setSubmitStatus({ text: "We could not save this warehouse right now.", type: "error" });
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitStatus({ text: "Submitting quote request...", type: "success" });

    try {
      await axios.post(buildApiUrl("/api/warehouseQuote"), quoteForm);
      setShowModal(false);
      setQuoteForm((previous) => ({
        ...initialQuote,
        warehouseName: previous.warehouseName,
        warehouseLocation: previous.warehouseLocation,
        warehouseArea: previous.warehouseArea,
        userId: previous.userId
      }));
      setSubmitStatus({ text: "Quote request submitted. Your CS team can now review this warehouse brief.", type: "success" });
    } catch (error) {
      console.error("Error creating quote request:", error);
      setSubmitStatus({ text: "We could not submit the quote request right now. Please try again.", type: "error" });
    }
  };

  if (!warehouse) {
    return <div className="page-loader">Loading warehouse details...</div>;
  }

  return (
    <>
      <Helmet>
        <title>{warehouse.name} | 3PL Search</title>
      </Helmet>

      <div className="warehouse-page">
        <section className="page-hero compact">
          <div className="container">
            <div className="warehouse-header-card">
              <div>
                <span className={`status-badge ${warehouse.status === "Currently Fulfillable" ? "active" : "inactive"}`}>
                  {warehouse.status || "Status pending"}
                </span>
                <h1>{warehouse.name}</h1>
                <div className="warehouse-meta-grid">
                  <p><FaLocationDot /> {warehouse.locations}</p>
                  <p><FaWarehouse /> {warehouse.totalArea} sq.ft</p>
                  <p><FaTruckFast /> {warehouse.services}</p>
                  <p><FaGlobe /> {warehouse.website}</p>
                </div>
              </div>
              {warehouse.image?.[0] ? (
                <img src={buildAssetUrl(warehouse.image[0])} alt={warehouse.name} />
              ) : (
                <div className="market-card-fallback large"><FaWarehouse /></div>
              )}
            </div>
          </div>
        </section>

        <section className="content-section">
          <div className="container">
            <div className="row g-4">
              <div className="col-lg-7">
                <div className="surface-card h-100">
                  <h3>Overview</h3>
                  <p>{warehouse.description}</p>
                  <h4>Detailed summary</h4>
                  <p>{warehouse.summary}</p>
                </div>
              </div>

              <div className="col-lg-5">
                <div className="surface-card h-100">
                  <h3>Capabilities snapshot</h3>
                  <div className="chip-list">
                    {formatList(warehouse.services).map((item) => <span className="badge-chip" key={item}>{item}</span>)}
                    {formatList(warehouse.products).map((item) => <span className="badge-chip soft" key={item}>{item}</span>)}
                  </div>
                  <div className="market-card-actions stacked">
                    <button className="site-primary-cta" onClick={() => setShowModal(true)}>Request quote for this warehouse</button>
                    <button className="site-secondary-cta" onClick={handleWishlist}><FaHeart /> Save warehouse</button>
                  </div>
                  {submitStatus ? <p className={`form-status ${submitStatus.type}`}>{submitStatus.text}</p> : null}
                </div>
              </div>
            </div>
          </div>
        </section>

        {showModal ? (
          <div className="modal-backdrop-custom">
            <div className="quote-modal">
              <div className="quote-modal-head">
                <div>
                  <span>Quote request</span>
                  <h3>{warehouse.name}</h3>
                </div>
                <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
              </div>

              <form onSubmit={handleSubmit}>
                <div className="row g-3">
                  <div className="col-md-6"><label>Contact name</label><input name="userName" value={quoteForm.userName} onChange={handleChange} required /></div>
                  <div className="col-md-6"><label>Email</label><input type="email" name="userEmail" value={quoteForm.userEmail} onChange={handleChange} required /></div>
                  <div className="col-md-6"><label>Phone</label><input name="userPhone" value={quoteForm.userPhone} onChange={handleChange} required /></div>
                  <div className="col-md-6"><label>Company</label><input name="companyName" value={quoteForm.companyName} onChange={handleChange} /></div>
                  <div className="col-md-6"><label>Company website</label><input name="companyWebsite" value={quoteForm.companyWebsite} onChange={handleChange} /></div>
                  <div className="col-md-6"><label>Sales channels</label><input name="salesChannel" value={quoteForm.salesChannel} onChange={handleChange} placeholder="Shopify, Amazon, wholesale" required /></div>
                  <div className="col-md-6"><label>SKU count</label><input type="number" name="totalSKU" value={quoteForm.totalSKU} onChange={handleChange} required /></div>
                  <div className="col-md-6"><label>Average monthly orders</label><input type="number" name="averageMonthlyOrders" value={quoteForm.averageMonthlyOrders} onChange={handleChange} required /></div>
                  <div className="col-md-6"><label>Primary service</label><select name="serviceNeeds" value={quoteForm.serviceNeeds} onChange={handleChange}><option value="">Select service</option>{serviceNeedOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                  <div className="col-md-6"><label>Product category</label><select name="productCategory" value={quoteForm.productCategory} onChange={handleChange}><option value="">Select category</option>{productCategoryOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                  <div className="col-md-6"><label>Storage type</label><select name="storageType" value={quoteForm.storageType} onChange={handleChange}><option value="">Select storage</option>{storageTypeOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                  <div className="col-md-6"><label>Temperature requirement</label><input name="temperatureRequirement" value={quoteForm.temperatureRequirement} onChange={handleChange} placeholder="Ambient / chilled / frozen" /></div>
                  <div className="col-md-6"><label>Regions to serve</label><input name="fulfillmentRegions" value={quoteForm.fulfillmentRegions} onChange={handleChange} placeholder="US East, EU, GCC" /></div>
                  <div className="col-md-6"><label>Preferred contact</label><select name="preferredContactMethod" value={quoteForm.preferredContactMethod} onChange={handleChange}>{contactMethodOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                  <div className="col-md-6"><label><FaCalendarDays /> Go-live timeline</label><select name="preferredGoLiveDate" value={quoteForm.preferredGoLiveDate} onChange={handleChange}><option value="">Select timeline</option>{timelineOptions.map((option) => <option key={option} value={option}>{option}</option>)}</select></div>
                  <div className="col-12"><label><FaSnowflake /> Additional notes</label><textarea rows="4" name="notes" value={quoteForm.notes} onChange={handleChange} placeholder="Mention compliance, packaging, returns, storage, inbound cadence, or any must-have service levels." /></div>
                </div>
                <div className="lead-form-actions">
                  <button className="site-primary-cta" type="submit">Submit quote request</button>
                  <button className="site-secondary-cta" type="button" onClick={() => setShowModal(false)}><FaPhone /> Cancel</button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}

export default WarehouseDetailsMarketplace;
